import { motion } from 'framer-motion'

// A photographed phone with the real SMS content laid over its screen.
//
// The alternative was baking the messages into an image, but then the thing
// the section is actually about -- "Dear Parent, Abena paid GHS 450..." --
// becomes unreadable pixels that no translator or screen reader can reach.
// So the photo supplies the frame and the hand, and the messages are live
// markup on top.
//
// SCREEN is measured from the source photo as percentages of its natural
// size (800x1066), so the overlay tracks the image at every width. The bounds
// are the lit screen itself -- top of the black camera bar to the bottom of
// the shutter bar, bezel excluded. If the photo is ever swapped, these four
// numbers have to be re-measured -- that is the cost of this approach, and the
// reason the box is defined once here rather than inline at the call site.
const SCREEN = { left: '38.1%', top: '34.9%', width: '27.9%', height: '37.4%' }

// What a parent sees as the sender is the school's own sender ID, set per
// school in Settings -- so this shows a school name rather than the product's.
// Deliberately a generic placeholder, not a real customer: this is a public
// marketing page, and naming one school here reads as a specific tenant rather
// than an illustration. mNotify caps alphanumeric sender IDs at 11 characters,
// which is the ceiling any replacement has to stay under.
const SENDER_ID = 'YOUR SCHOOL'

const MESSAGES = [
  { time: '09:14', text: 'Dear Parent, Abena paid GHS 450 for Week 8 feeding. Balance: GHS 0.' },
  { time: '09:14', text: 'Reminder: Kwame Asante has an outstanding balance of GHS 350. Please pay by Friday.' },
  { time: '08:00', text: "Announcement: School closes Friday 30th for Founder's Day." },
]

export default function SmsPhoneMockup() {
  // containerType makes the cqw units below resolve against this box, so the
  // overlay text scales with the photo rather than with the viewport.
  return (
    <div className="relative w-full max-w-sm mx-auto select-none" style={{ containerType: 'inline-size' }}>
      <img
        src="https://images.unsplash.com/photo-1565849904461-04a58ad377e0?auto=format&fit=crop&w=800&q=80"
        alt="A parent holding a phone showing SMS messages from their child's school"
        className="w-full h-auto rounded"
        loading="lazy"
        decoding="async"
        width={800}
        height={1066}
      />

      {/* The screen. aria-hidden on the frame only; the messages below stay
          readable, since they are the point of the section. */}
      <div
        className="absolute overflow-hidden flex flex-col"
        style={{
          ...SCREEN,
          background: 'linear-gradient(180deg, #0C1512 0%, #101D18 100%)',
          borderRadius: '2.5%/1.6%',
          padding: '3.5% 3.5% 2.5%',
          gap: '3%',
        }}
      >
        <div
          className="flex items-center justify-between shrink-0"
          style={{ fontSize: 'clamp(5px, 1.5cqw, 8px)', color: 'var(--panel-dark-mute, #7A8F88)' }}
        >
          <span className="font-boxy font-bold truncate" style={{ color: 'var(--accent-strong, #15B37F)' }}>{SENDER_ID}</span>
          <span>9:41</span>
        </div>

        <div className="flex flex-col gap-[4%] overflow-hidden">
          {MESSAGES.map((m, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 6 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.35, delay: 0.2 + i * 0.15 }}
              className="rounded leading-snug"
              style={{
                background: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.09)',
                color: '#CBD8D3',
                padding: '4% 5%',
                fontSize: 'clamp(4.5px, 1.45cqw, 7.5px)',
              }}
            >
              {m.text}
            </motion.p>
          ))}
        </div>
      </div>
    </div>
  )
}
